/**
 * Firebase configuration without importing the Firebase SDK.
 * Use this module in login/layout code so auth never loads Firebase at import time.
 *
 * Local OAuth (Google/Apple): add `localhost`, `127.0.0.1`, and `enthioshub.com` under
 * Firebase Console → Authentication → Settings → Authorized domains.
 * Admin portal login does not use Firebase OAuth.
 */
/**
 * Resolves Firebase authDomain based on environment variable or active host:
 * - Staging: stagingfrontend.enthioshub.com
 * - Production: enthioshub.com
 */
export function getFirebaseAuthDomain(): string {
  const envVal = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN?.trim();
  if (envVal) {
    return envVal;
  }

  if (typeof window !== 'undefined') {
    const runtimeConfig =
      (window as any).__ENTHIOS_RUNTIME__ || (window as any).RUNTIME_CONFIG;
    if (runtimeConfig?.firebaseAuthDomain && typeof runtimeConfig.firebaseAuthDomain === 'string') {
      return runtimeConfig.firebaseAuthDomain.trim();
    }

    const host = String(
      (window as any).__ENTHIOS_TEST_HOSTNAME__ || window.location?.hostname || ''
    ).toLowerCase();
    if (host === 'stagingfrontend.enthioshub.com' || host.startsWith('stagingfrontend.')) {
      return 'stagingfrontend.enthioshub.com';
    }
    if (host === 'enthioshub.com' || host === 'www.enthioshub.com') {
      return 'enthioshub.com';
    }
  }

  return 'enthioshub.com';
}

export const firebasePublicConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || 'AIzaSyCFpc4bLHq2LD3EgIuBdSAo-xqqKzdsw_E',
  get authDomain(): string {
    return getFirebaseAuthDomain();
  },
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'EnthiosHub-567c0',
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || 'EnthiosHub-567c0.firebasestorage.app',
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '217920571835',
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '1:217920571835:web:e503e25c99f943ec4d5c9e',
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || 'G-TNRJZDXXE7',
};

export const appleSignInEnabled = process.env.NEXT_PUBLIC_ENABLE_APPLE_SIGN_IN === 'true';

/** Set NEXT_PUBLIC_FIREBASE_ENABLED=false to disable all Firebase SDK startup (auth still uses API). */
export function firebaseEnabled(): boolean {
  const flag = (process.env.NEXT_PUBLIC_FIREBASE_ENABLED ?? 'true').trim().toLowerCase();
  if (flag === 'false' || flag === '0' || flag === 'off') {
    return false;
  }

  const apiKey = firebasePublicConfig.apiKey.trim();
  const authDomain = firebasePublicConfig.authDomain.trim();
  const projectId = firebasePublicConfig.projectId.trim();
  const appId = firebasePublicConfig.appId.trim();

  return Boolean(apiKey && authDomain && projectId && appId);
}

/** Analytics is opt-in — prevents Firebase Installations 403 noise when API is not configured. */
export function firebaseAnalyticsEnabled(): boolean {
  if (!firebaseEnabled()) return false;
  const flag = (process.env.NEXT_PUBLIC_FIREBASE_ANALYTICS_ENABLED ?? 'false').trim().toLowerCase();
  return flag === 'true' || flag === '1' || flag === 'on';
}
